/**
 * templates/categories/content.js - Content Rails & Grid Templates
 * Location: /app/frontend/src/admin/page_builder/templates/categories/
 * 
 * Content presentation templates for rails, grids, and collections.
 * Uses Phase 1 primitives: layout presets, card variants.
 */

import { LayoutPreset } from '../../layout/presets';
import { BackgroundType } from '../../layout/backgrounds';
import { CardVariant } from '../../cards/variants';

/**
 * Trending Rail - Horizontal scroll of trending content
 * Use cases: Homepage trending section, hot picks
 */
export const trending_rail = {
  id: 'trending_rail',
  name: 'Trending Rail',
  category: 'content',
  description: 'Horizontal scrolling rail of trending content. Shows popularity indicators.',
  thumbnail: null,
  
  layout: { preset: LayoutPreset.SINGLE },
  background: { type: BackgroundType.NONE },
  cardVariant: CardVariant.DISCOVERY,
  
  // Template constraints
  allowedLayouts: [LayoutPreset.SINGLE],
  allowedBackgrounds: [BackgroundType.NONE, BackgroundType.SOLID],
  allowedCardVariants: [CardVariant.DISCOVERY, CardVariant.FEATURED],
  maxBlocks: 3, // Can have up to 3 trending rails
  
  // Defaults
  defaultLayout: { preset: LayoutPreset.SINGLE },
  defaultBackground: { type: BackgroundType.NONE },
  
  blockType: 'section_trending_rail',
  defaultProps: {
    heading: 'Trending Now',
    showHeading: true,
    contentType: 'all', // 'all', 'comics', 'novels', 'artwork'
    maxItems: 12,
    showRank: true,
    autoScroll: false,
  },
  
  editableFields: [
    { key: 'heading', label: 'Section Heading', type: 'text' },
    { key: 'showHeading', label: 'Show Heading', type: 'boolean' },
    { key: 'contentType', label: 'Content Type', type: 'select', options: ['all', 'comics', 'novels', 'artwork'] },
    { key: 'maxItems', label: 'Max Items', type: 'number', min: 4, max: 20 },
    { key: 'showRank', label: 'Show Rank Numbers', type: 'boolean' },
    { key: 'autoScroll', label: 'Auto Scroll', type: 'boolean' },
  ],
  
  // Data source hint (for content fetching layer)
  dataSource: {
    type: 'api',
    endpoint: '/api/content/trending',
    params: ['contentType', 'maxItems'],
  },
};

/**
 * Featured Grid - 3-4 column grid of featured/promoted items
 * Use cases: Featured content section, editorial picks
 */
export const featured_grid = {
  id: 'featured_grid',
  name: 'Featured Grid',
  category: 'content',
  description: 'Multi-column grid of featured content with larger card treatment.',
  thumbnail: null,
  
  layout: { preset: LayoutPreset.SINGLE },
  background: { type: BackgroundType.NONE },
  cardVariant: CardVariant.FEATURED,
  
  // Template constraints
  allowedLayouts: [LayoutPreset.SINGLE],
  allowedBackgrounds: [BackgroundType.NONE, BackgroundType.SOLID, BackgroundType.GRADIENT],
  allowedCardVariants: [CardVariant.FEATURED, CardVariant.DISCOVERY],
  maxBlocks: 2, // Can have up to 2 featured grids
  
  // Defaults
  defaultLayout: { preset: LayoutPreset.SINGLE },
  defaultBackground: { type: BackgroundType.NONE },
  
  blockType: 'section_featured_grid',
  defaultProps: {
    heading: 'Featured',
    showHeading: true,
    columns: 3,
    contentType: 'all',
    maxItems: 6,
    showDescription: true,
  },
  
  editableFields: [
    { key: 'heading', label: 'Section Heading', type: 'text' },
    { key: 'showHeading', label: 'Show Heading', type: 'boolean' },
    { key: 'columns', label: 'Columns', type: 'number', min: 2, max: 4 },
    { key: 'contentType', label: 'Content Type', type: 'select', options: ['all', 'comics', 'novels', 'artwork'] },
    { key: 'maxItems', label: 'Max Items', type: 'number', min: 3, max: 12 },
    { key: 'showDescription', label: 'Show Description', type: 'boolean' },
  ],
  
  dataSource: {
    type: 'api',
    endpoint: '/api/content/featured',
    params: ['contentType', 'maxItems'],
  },
};

/**
 * New Releases Rail - Latest published content
 * Use cases: Homepage new releases, recent additions
 */
export const new_releases_rail = {
  id: 'new_releases_rail',
  name: 'New Releases',
  category: 'content',
  description: 'Horizontal rail showcasing newly published content.',
  thumbnail: null,
  
  layout: { preset: LayoutPreset.SINGLE },
  background: { type: BackgroundType.NONE },
  cardVariant: CardVariant.DISCOVERY,
  
  // Template constraints
  allowedLayouts: [LayoutPreset.SINGLE],
  allowedBackgrounds: [BackgroundType.NONE, BackgroundType.SOLID],
  allowedCardVariants: [CardVariant.DISCOVERY, CardVariant.READING],
  maxBlocks: 2,
  
  // Defaults
  defaultLayout: { preset: LayoutPreset.SINGLE },
  defaultBackground: { type: BackgroundType.NONE },
  
  blockType: 'section_new_releases_rail',
  defaultProps: {
    heading: 'New Releases',
    showHeading: true,
    contentType: 'all',
    maxItems: 12,
    showPublishDate: true,
    timeRange: '7d', // '24h', '7d', '30d'
  },
  
  editableFields: [
    { key: 'heading', label: 'Section Heading', type: 'text' },
    { key: 'showHeading', label: 'Show Heading', type: 'boolean' },
    { key: 'contentType', label: 'Content Type', type: 'select', options: ['all', 'comics', 'novels', 'artwork'] },
    { key: 'maxItems', label: 'Max Items', type: 'number', min: 4, max: 20 },
    { key: 'showPublishDate', label: 'Show Publish Date', type: 'boolean' },
    { key: 'timeRange', label: 'Time Range', type: 'select', options: ['24h', '7d', '30d'] },
  ],
  
  dataSource: {
    type: 'api',
    endpoint: '/api/content/new',
    params: ['contentType', 'maxItems', 'timeRange'],
  },
};

/**
 * Continue Reading - User's in-progress content
 * Use cases: Personalized homepage section, reading history
 */
export const continue_reading = {
  id: 'continue_reading',
  name: 'Continue Reading',
  category: 'content',
  description: 'Shows user\'s in-progress content with reading progress indicators.',
  thumbnail: null,
  
  layout: { preset: LayoutPreset.SINGLE },
  background: { type: BackgroundType.NONE },
  cardVariant: CardVariant.READING,
  
  // Template constraints
  allowedLayouts: [LayoutPreset.SINGLE],
  allowedBackgrounds: [BackgroundType.NONE],
  allowedCardVariants: [CardVariant.READING],
  maxBlocks: 1, // Only one continue reading section
  
  // Defaults
  defaultLayout: { preset: LayoutPreset.SINGLE },
  defaultBackground: { type: BackgroundType.NONE },
  
  blockType: 'section_continue_reading',
  defaultProps: {
    heading: 'Continue Reading',
    showHeading: true,
    maxItems: 6,
    showProgress: true,
    showLastRead: true,
    emptyMessage: 'Start reading to see your progress here!',
  },
  
  editableFields: [
    { key: 'heading', label: 'Section Heading', type: 'text' },
    { key: 'showHeading', label: 'Show Heading', type: 'boolean' },
    { key: 'maxItems', label: 'Max Items', type: 'number', min: 3, max: 12 },
    { key: 'showProgress', label: 'Show Progress Bar', type: 'boolean' },
    { key: 'showLastRead', label: 'Show Last Read Time', type: 'boolean' },
    { key: 'emptyMessage', label: 'Empty State Message', type: 'text' },
  ],
  
  dataSource: {
    type: 'api',
    endpoint: '/api/user/reading-progress',
    params: ['maxItems'],
    requiresAuth: true,
  },
};

/**
 * Genre Spotlight - Curated genre collection
 * Use cases: Genre browsing, category highlights
 */
export const genre_spotlight = {
  id: 'genre_spotlight',
  name: 'Genre Spotlight',
  category: 'content',
  description: 'Curated collection of content from a specific genre.',
  thumbnail: null,
  
  layout: { preset: LayoutPreset.SINGLE },
  background: { type: BackgroundType.NONE },
  cardVariant: CardVariant.DISCOVERY,
  
  // Template constraints
  allowedLayouts: [LayoutPreset.SINGLE],
  allowedBackgrounds: [BackgroundType.NONE, BackgroundType.SOLID, BackgroundType.GRADIENT],
  allowedCardVariants: [CardVariant.DISCOVERY, CardVariant.FEATURED],
  maxBlocks: 5, // Can have multiple genre spotlights
  
  // Defaults
  defaultLayout: { preset: LayoutPreset.SINGLE },
  defaultBackground: { type: BackgroundType.NONE },
  
  blockType: 'section_genre_spotlight',
  defaultProps: {
    heading: 'Action & Adventure',
    showHeading: true,
    genre: '', // Genre slug/id
    maxItems: 8,
    showGenreIcon: true,
    linkToGenrePage: true,
  },
  
  editableFields: [
    { key: 'heading', label: 'Section Heading', type: 'text' },
    { key: 'showHeading', label: 'Show Heading', type: 'boolean' },
    { key: 'genre', label: 'Genre', type: 'text' },
    { key: 'maxItems', label: 'Max Items', type: 'number', min: 4, max: 16 },
    { key: 'showGenreIcon', label: 'Show Genre Icon', type: 'boolean' },
    { key: 'linkToGenrePage', label: 'Link to Genre Page', type: 'boolean' },
  ],
  
  dataSource: {
    type: 'api',
    endpoint: '/api/content/by-genre',
    params: ['genre', 'maxItems'],
  },
};

/**
 * Community Picks - User-voted or curated selections
 * Use cases: Community favorites, staff picks
 */
export const community_picks = {
  id: 'community_picks',
  name: 'Community Picks',
  category: 'content',
  description: 'Community-curated or staff-selected content highlights.',
  thumbnail: null,
  
  layout: { preset: LayoutPreset.SINGLE },
  background: { type: BackgroundType.NONE },
  cardVariant: CardVariant.DISCOVERY,
  
  // Template constraints
  allowedLayouts: [LayoutPreset.SINGLE],
  allowedBackgrounds: [BackgroundType.NONE, BackgroundType.SOLID],
  allowedCardVariants: [CardVariant.DISCOVERY, CardVariant.FEATURED],
  maxBlocks: 2,
  
  // Defaults
  defaultLayout: { preset: LayoutPreset.SINGLE },
  defaultBackground: { type: BackgroundType.NONE },
  
  blockType: 'section_community_picks',
  defaultProps: {
    heading: 'Community Picks',
    showHeading: true,
    curationType: 'community', // 'community', 'staff', 'editors'
    maxItems: 8,
    showVoteCount: false,
    showCurator: true,
  },
  
  editableFields: [
    { key: 'heading', label: 'Section Heading', type: 'text' },
    { key: 'showHeading', label: 'Show Heading', type: 'boolean' },
    { key: 'curationType', label: 'Curation Type', type: 'select', options: ['community', 'staff', 'editors'] },
    { key: 'maxItems', label: 'Max Items', type: 'number', min: 4, max: 16 },
    { key: 'showVoteCount', label: 'Show Vote Count', type: 'boolean' },
    { key: 'showCurator', label: 'Show Curator', type: 'boolean' },
  ],
  
  dataSource: {
    type: 'api',
    endpoint: '/api/content/curated',
    params: ['curationType', 'maxItems'],
  },
};

/**
 * Art Gallery Grid - Masonry or uniform grid for artwork showcase
 * Use cases: Art portfolio, gallery page, artwork collection
 */
export const art_gallery_grid = {
  id: 'art_gallery_grid',
  name: 'Art Gallery Grid',
  category: 'gallery',
  description: 'Masonry or uniform grid layout for artwork showcase.',
  thumbnail: null,
  
  layout: { preset: LayoutPreset.SINGLE },
  background: { type: BackgroundType.NONE },
  cardVariant: CardVariant.DISCOVERY,
  
  // Template constraints
  allowedLayouts: [LayoutPreset.SINGLE],
  allowedBackgrounds: [BackgroundType.NONE, BackgroundType.SOLID],
  allowedCardVariants: [CardVariant.DISCOVERY],
  maxBlocks: 2,
  
  // Defaults
  defaultLayout: { preset: LayoutPreset.SINGLE },
  defaultBackground: { type: BackgroundType.NONE },
  
  blockType: 'section_art_gallery_grid',
  defaultProps: {
    heading: 'Gallery',
    showHeading: true,
    gridStyle: 'masonry', // 'masonry', 'uniform', 'featured'
    columns: 3,
    maxItems: 12,
    showTitle: true,
    showArtist: true,
    showLikes: false,
    lightboxEnabled: true, // Click to view full size
    creatorId: '', // Optional: filter by creator
  },
  
  editableFields: [
    { key: 'heading', label: 'Section Heading', type: 'text' },
    { key: 'showHeading', label: 'Show Heading', type: 'boolean' },
    { key: 'gridStyle', label: 'Grid Style', type: 'select', options: ['masonry', 'uniform', 'featured'] },
    { key: 'columns', label: 'Columns', type: 'number', min: 2, max: 5 },
    { key: 'maxItems', label: 'Max Items', type: 'number', min: 4, max: 24 },
    { key: 'showTitle', label: 'Show Title', type: 'boolean' },
    { key: 'showArtist', label: 'Show Artist', type: 'boolean' },
    { key: 'showLikes', label: 'Show Likes', type: 'boolean' },
    { key: 'lightboxEnabled', label: 'Enable Lightbox', type: 'boolean' },
  ],
  
  dataSource: {
    type: 'api',
    endpoint: '/api/artwork',
    params: ['creatorId', 'maxItems'],
  },
};

export default {
  trending_rail,
  featured_grid,
  new_releases_rail,
  continue_reading,
  genre_spotlight,
  community_picks,
  art_gallery_grid,
};
